/**
 * Press Releases Load More
 * Handles dynamic loading of press releases via AJAX
 */
export function initPressReleasesLoadMore() {
    const container = document.querySelector('[data-press-releases-container]');
    if (!container) return;

    const loadingState = container.querySelector('[data-loading-state]');
    const itemsList = container.querySelector('[data-press-releases-list]');
    const emptyState = container.querySelector('[data-empty-state]');
    const paginationContainer = container.querySelector('[data-pagination-container]');
    const loadMoreBtn = container.querySelector('[data-load-more-btn]');
    
    if (!itemsList) return;

    // Get dictionary values from global object or use fallbacks
    const dictionary = window.dictionary || {
        loading: 'Loading...',
        loadMore: 'Load More',
        readMore: 'Read more',
        failedToLoadPressReleases: 'Failed to load press releases. Please refresh the page to try again.',
        failedToLoadMorePressReleases: 'Failed to load more press releases. Please try again.'
    };

    // Get initial configuration from data attributes
    const isRegulatory = container.dataset.isRegulatory === 'true';
    const searchQuery = container.dataset.searchQuery || '';
    const initialOffset = parseInt(container.dataset.initialOffset || '0');
    const limit = parseInt(container.dataset.limit || '20');
    const hasServerRenderedItems = container.dataset.serverRendered === 'true';

    let currentOffset = initialOffset;
    let isLoading = false;

    // Handle year dropdown navigation
    const yearSelect = container.querySelector('[data-year-select]');
    if (yearSelect) {
        yearSelect.addEventListener('change', () => {
            const params = new URLSearchParams(window.location.search);
            if (yearSelect.value) {
                params.set('year', yearSelect.value);
            } else {
                params.delete('year');
            }
            window.location.search = params.toString();
        });
    }

    // Load initial press releases on page load only when server-side render did not provide items
    if (!hasServerRenderedItems) {
        loadPressReleases(currentOffset, true);
    }

    // Handle load more button clicks
    if (loadMoreBtn) {
        loadMoreBtn.addEventListener('click', async (e) => {
            e.preventDefault();
            
            if (isLoading) return;
            
            await loadPressReleases(currentOffset, false);
        });
    }

    /**
     * Load press releases from API
     * @param {number} offset - The offset for pagination
     * @param {boolean} isInitialLoad - Whether this is the initial load
     */
    async function loadPressReleases(offset, isInitialLoad = false) {
        if (isLoading) return;
        
        isLoading = true;
        
        // Show loading state for initial load, disable button for subsequent loads
        if (isInitialLoad && loadingState) {
            loadingState.style.display = 'block';
            loadingState.setAttribute('aria-busy', 'true');
        } else if (loadMoreBtn) {
            loadMoreBtn.classList.add('opacity-50', 'cursor-not-allowed');
            loadMoreBtn.disabled = true;
            loadMoreBtn.setAttribute('aria-busy', 'true');
            loadMoreBtn.textContent = dictionary.loading;
        }
        
        try {
            // Build API URL
            const apiUrl = new URL('/api/press-releases/load-more', window.location.origin);
            apiUrl.searchParams.append('offset', offset.toString());
            apiUrl.searchParams.append('limit', limit.toString());
            apiUrl.searchParams.append('regulatory', isRegulatory.toString());
            
            // Add search query if present
            if (searchQuery) {
                apiUrl.searchParams.append('search', searchQuery);
            }
            
            // Add language from document
            const lang = document.documentElement.lang || 'en';
            apiUrl.searchParams.append('lang', lang);
            
            // Add year from query params if present
            const urlParams = new URLSearchParams(window.location.search);
            const year = urlParams.get('year');
            if (year) {
                apiUrl.searchParams.append('year', year);
            }

            const response = await fetch(apiUrl.toString(), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to load press releases');
            }

            const data = await response.json();
            
            // Hide loading state
            if (loadingState) {
                loadingState.style.display = 'none';
                loadingState.setAttribute('aria-busy', 'false');
            }
            
            // Show the items list
            itemsList.style.display = '';
            
            // Render items
            if (data.items && data.items.length > 0) {
                const fragment = document.createDocumentFragment();
                let firstNewItem = null;
                
                data.items.forEach((item, index) => {
                    const itemElement = createPressReleaseItem(item, dictionary);
                    if (index === 0 && !isInitialLoad) {
                        firstNewItem = itemElement;
                    }
                    fragment.appendChild(itemElement);
                });
                
                itemsList.appendChild(fragment);
                
                // Focus on first new item for keyboard users (but not on initial load)
                if (firstNewItem && !isInitialLoad) {
                    firstNewItem.focus();
                }
                
                // Update offset for next load
                if (data.nextOffset) {
                    currentOffset = parseInt(data.nextOffset);
                }
                
                // Show/hide load more button
                if (paginationContainer && loadMoreBtn) {
                    if (data.hasMore) {
                        paginationContainer.style.display = '';
                    } else {
                        paginationContainer.style.display = 'none';
                    }
                }
            } else if (isInitialLoad) {
                // Show empty state if no items on initial load
                itemsList.style.display = 'none';
                if (emptyState) {
                    emptyState.style.display = '';
                }
            }

        } catch (error) {
            console.error('Error loading press releases:', error);
            
            // Hide loading state
            if (loadingState) {
                loadingState.style.display = 'none';
                loadingState.setAttribute('aria-busy', 'false');
            }
            
            if (isInitialLoad && emptyState) {
                emptyState.style.display = '';
                const errorMessage = emptyState.querySelector('p');
                if (errorMessage) {
                    errorMessage.textContent = dictionary.failedToLoadPressReleases;
                }
            } else {
                alert(dictionary.failedToLoadMorePressReleases);
            }
        } finally {
            isLoading = false;
            
            // Reset load more button
            if (loadMoreBtn) {
                loadMoreBtn.classList.remove('opacity-50', 'cursor-not-allowed');
                loadMoreBtn.disabled = false;
                loadMoreBtn.removeAttribute('aria-busy');
                loadMoreBtn.textContent = dictionary.loadMore;
            }
        }
    }
}

/**
 * Create a press release item element from data
 * @param {object} item - Press release item data
 * @param {object} dictionary - Dictionary object with translated text
 */
function createPressReleaseItem(item, dictionary) {
    const article = document.createElement('article');
    article.className = 'clamp-[py,32,40] first:pt-0 press-release-item';

    const a = document.createElement('a');
    a.className = 'group block';
    a.href = `${window.settings.pressReleasesPageUrl}/${item.slug}`;

    // Title
    const h2 = document.createElement('h2');
    h2.className = 'clamp-[text,24px,32px] font-bold clamp-[mb,16,20] leading-[1.2]';
    h2.textContent = item.title || '';
    a.appendChild(h2);

    // Date and Tag
    const dateP = document.createElement('p');
    dateP.className = 'clamp-[text,14px,15px] font-bold clamp-[mb,16,20]';
    if (item.publishDate) {
        const date = new Date(item.publishDate);
        const time = document.createElement('time');
        time.dateTime = date.toISOString().split('T')[0];
        time.textContent = formatDate(date);
        dateP.appendChild(time);

        if (item.isRegulatory) {
            const separator = document.createElement('span');
            separator.className = 'mx-6';
            separator.setAttribute('aria-hidden', 'true');
            separator.textContent = '—';
            dateP.appendChild(separator);

            const tag = document.createElement('span');
            tag.className = 'font-bold uppercase text-black/48';
            tag.textContent = dictionary.regulatory;
            dateP.appendChild(tag);
        }
    }
    a.appendChild(dateP);

    // Body Text / Summary
    if (item.html) {
        const bodyText = stripHtml(item.html).substring(0, 200);
        if (bodyText) {
            const p = document.createElement('p');
            p.className = 'clamp-[text,15px,17px] leading-[1.6] text-black/87 clamp-[mb,16,20]';
            p.textContent = bodyText + (item.html.length > 200 ? '...' : '');
            a.appendChild(p);
        }
    }

    const readMoreLabel = createReadMoreLabel(dictionary);
    a.appendChild(readMoreLabel);

    article.appendChild(a);
    return article;
}

/**
 * Create "Read more" label element (span for use inside anchor)
 * @param {object} dictionary - Dictionary object with translated text
 */
function createReadMoreLabel(dictionary) {
    const span = document.createElement('span');
    span.className = 'inline-flex u_small items-end gap-10 font-bold text-link group-hover:text-gold duration-300 transition uppercase clamp-[text,13px,15px]'

    const textNode = document.createTextNode(dictionary.readMore + ' ');
    span.appendChild(textNode);

    const iconSpan = document.createElement('span');
    iconSpan.className = 'text-link-icon';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'group-hover:translate-x-8 transition duration-300');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('width', '36');
    svg.setAttribute('height', '24');
    svg.setAttribute('viewBox', '0 0 36 24');
    svg.setAttribute('fill', 'none');

    const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path1.setAttribute('d', 'M5 12L31 12');
    path1.setAttribute('class', 'group-hover:!stroke-gold transition duration-300');
    path1.setAttribute('stroke', 'black');
    path1.setAttribute('stroke-width', '2');
    path1.setAttribute('stroke-linecap', 'round');
    path1.setAttribute('stroke-linejoin', 'round');

    const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path2.setAttribute('d', 'M24 5L31 12L24 19');
    path2.setAttribute('class', 'group-hover:!stroke-gold transition duration-300');
    path2.setAttribute('stroke', 'black');
    path2.setAttribute('stroke-width', '2');
    path2.setAttribute('stroke-linecap', 'round');
    path2.setAttribute('stroke-linejoin', 'round');

    svg.appendChild(path1);
    svg.appendChild(path2);
    iconSpan.appendChild(svg);
    span.appendChild(iconSpan);

    return span;
}

/**
 * Strip HTML tags from string
 */
function stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
}

/**
 * Format date as "February 4, 2026"
 */
function formatDate(date) {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const locale = document.documentElement.lang || 'en';
    return date.toLocaleDateString(locale, options);
}
